'use client';

import { Fragment, useMemo, useState } from 'react';
import Link from 'next/link';
import { CardProps } from './types';

import DirectusImage from '../image/DirectusImage';

import TitleH1 from '../common/TitleH1';
import ButtonLink from '../link/ButtonLink';
import Ball from '../icon/Ball';
import Paragraph from '../common/Paragraph';
import WysiwygBlock from '../blocks/WysiwygBlock';
import FaqItem from '@/components/item/FaqItem';

export default function CardFaq({
  callToActionUrl,
  displayDescription = true,
  displayButton = true,
  eyebrow_text,
  title,
  description,
  wisiwyg,
  questions,
  isOpen,
  isAllOpen,
  image,
  imageAlt,
  imageBlur,
}: CardProps) {
  const link = callToActionUrl.external
    ? callToActionUrl.url
    : `${callToActionUrl.url}/${callToActionUrl.slug}`;
  const time = new Date().getTime();
  const accordeonId = useMemo(() => time, [time]);
  const hasbutton =
    displayButton && callToActionUrl && (callToActionUrl.url || callToActionUrl.slug);

  const [openIndexes, setOpenIndexes] = useState<number[]>(() => {
    if (isAllOpen && questions) {
      return questions.map((question, i) => i);
    }
    return isOpen ? [0] : [];
  });

  const handleToggle = (index: number) => {
    setOpenIndexes((prev) => {
      if (isAllOpen) {
        return prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index];
      }
      return prev.includes(index) ? [] : [index];
    });
  };

  const panel = (
    <div className="flex flex-col gap-3 lg:flex-row lg:block_second">
      <div
        data-test-id="card-faq"
        className={`card flex w-full flex-col rounded-md bg-gradient-to-r from-sky-200 to-pink-200 py-10 bg-card-faq-ellipse lg:card-side md:min-w-[1000px] md:flex-row md:p-20`}
      >
        <div className="flex w-full flex-col px-5 md:w-7/12">
          {eyebrow_text && (
            <div className="inline-block pb-[30px] text-xs font-bold uppercase text-gray-400">
              <span className="mr-2 inline-block align-sub">
                <Ball animated={false} />
              </span>
              {eyebrow_text}
            </div>
          )}
          {title && (
            <TitleH1 color="black" heading={false} size="big" styles="mb-4">
              {title}
            </TitleH1>
          )}
          <div className="grow">
            <>
              {displayDescription && (
                <Paragraph styles="leading-10 text-2xl">{description}</Paragraph>
              )}
              <>
                <WysiwygBlock html={wisiwyg || ''} />
              </>
              {image && (
                <div className="mt-10 flex justify-center">
                  <DirectusImage src={image || ''} alt={imageAlt || ''} blur={imageBlur} />
                </div>
              )}
              {hasbutton && (
                <div className="md:-ml-2.5">
                  <ButtonLink
                    url={callToActionUrl}
                    name={callToActionUrl.linkText || ''}
                    styles="my-3 text-slate-950 text-center"
                  />
                </div>
              )}
            </>
          </div>
        </div>
        <div className="flex w-full flex-col px-5 md:w-7/12">
          {questions?.map(({ question, answer }, index) => (
            <Fragment key={index}>
              <FaqItem
                id={accordeonId}
                index={index}
                question={question}
                answer={answer}
                isOpen={openIndexes.includes(index)}
                handleToggle={handleToggle}
              />
              <div
                className={`ml-4 mr-7 h-[0.5px] w-[100%-100px] bg-[#8A8FA266] ${index !== questions.length - 1 && 'mb-5'}`}
              ></div>
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );

  return displayButton ? panel : <Link href={link}>{panel}</Link>;
}
